import React, { useEffect, useState } from 'react';
import './FolderConfig.scss';
import useToast from '../../../helpers/useToast';
import { Avatar } from '@appkit4/react-components/avatar';
import useGenericErrorToast from '../../../helpers/useGenericErrorToast';
import {
    useGetV1FolderFolderIdPotentialUsers,
    usePutV1FolderFolderIdDeletePersona,
    usePostV1FolderFolderIdAddFolderPersonas,
    usePutV1FolderFolderIdModifyFolderPersonas,
} from '../../../api/generated/endpoint';
import {
    PotentialFolderUser,
    PutV1FolderDeletePersonaParams,
    FolderPersonaAssignmentDTO,
    AddFolderPersonasRequest,
    ModifyFolderPersonasRequest,
} from '../../../api/generated/models';
import useLocale from '../../../locale/useLocale';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Search, Select, Tooltip } from '@appkit4/react-components';
import SolutionCenter from '../../../assets/SolutionCenter.svg';
import { DeleteModal } from '../../shared/deleteModal';
import { RadioGroup, Radio } from '@appkit4/react-components/radio';

type Member = {
    id: number;
    name: string | null;
    email: string;
    persona: 'admin' | 'user';
};

type addedMember = {
    id: number;
    name: string | null;
    email: string;
    persona: 'admin' | 'user';
    parentPersona?: 'admin' | 'user' | 'member' | undefined;
    newPersona?: 'admin' | 'user';
    folderPersonaId?: number; // Add this to track original folder persona
};

type FolderConfigPopupProps = {
    folderId: number;
    visible: boolean;
    folderData: {
        name: string;
        members: addedMember[];
    };
    allUsers: addedMember[]; // Full list including members (folderPersonaId = 3)
    onClose: () => void;
    onSuccess?: () => void;
};

const FolderConfig: React.FC<FolderConfigPopupProps> = ({
    folderId,
    visible,
    folderData,
    allUsers,
    onClose,
    onSuccess,
}) => {
    const showToast = useToast();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();
    const [members, setMembers] = useState<addedMember[]>(folderData.members);
    const [newMemberId, setNewMemberId] = useState<number | ''>('');
    const [potentialMembers, setPotentialMembers] = useState<addedMember[]>([]);
    const [deleteMemberId, setDeleteMemberId] = useState<number | ''>('');
    const [visibleDeleteModal, setVisibleDeleteModal] =
        useState<boolean>(false);
    const [deleteAll, setDeleteAll] = useState(0);

    const { data: potentialData } =
        useGetV1FolderFolderIdPotentialUsers(folderId);
    const { mutate: deletePersona } = usePutV1FolderFolderIdDeletePersona();
    const { mutate: addFolderPersonas } =
        usePostV1FolderFolderIdAddFolderPersonas();
    const { mutate: modifyFolderPersonas } =
        usePutV1FolderFolderIdModifyFolderPersonas();

    const roleOptions = [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
    ];
    const [folderRole, setFolderRole] = useState(roleOptions[0].value);

    useEffect(() => {
        setMembers(folderData.members);
    }, [folderData]);

    useEffect(() => {
        if (potentialData) {
            const validPotentialMembers = potentialData.potentialUsers
                .filter(
                    (user: PotentialFolderUser) =>
                        user.userId !== undefined && user.fullName !== undefined
                )
                .filter((user: PotentialFolderUser) => {
                    // Exclude users who are already admin or user (but allow members to be promoted)
                    const existingUser = allUsers.find(
                        (u) => u.id === user.userId
                    );
                    return !existingUser || existingUser.folderPersonaId === 3; // Allow members (folderPersonaId = 3) to be promoted
                })
                .map(
                    (user: PotentialFolderUser): Member => ({
                        email: user.email || '',
                        id: user.userId as number,
                        name: user.fullName as string,
                        persona: 'user', // Default role
                    })
                );

            setPotentialMembers(validPotentialMembers);
        }
    }, [potentialData, allUsers]);

    const handlePersonaChange = (
        memberId: number,
        newPersona: 'admin' | 'user'
    ) => {
        setMembers(
            members.map((member) =>
                member.id === memberId
                    ? { ...member, newPersona: newPersona }
                    : member
            )
        );
    };

    const openDeleteModal = (member: any) => {
        if (cannotDeleteCondition(member)) return;
        setDeleteMemberId(member.id);
        setVisibleDeleteModal(true);
    };

    const cannotDeleteCondition = (member: any) => {
        return !(
            member.parentPersona == 'member' ||
            member.parentPersona == undefined
        );
    };

    const canChangeRoleCondition = (member: any) => {
        return (
            (member.parentPersona == 'user' && member.persona == 'user') ||
            (member.parentPersona != 'admin' && member.persona == 'admin') ||
            member.parentPersona === undefined
        );
    };

    const handleAddMember = () => {
        if (newMemberId !== '') {
            const newMember = potentialMembers.find(
                (member) => member.id === newMemberId
            );
            if (newMember) {
                const assignment: FolderPersonaAssignmentDTO = {
                    userId: newMember.id,
                    folderPersona:
                        folderRole === 'admin'
                            ? 1
                            : folderRole === 'user'
                            ? 2
                            : 3,
                };

                // Check if user is already a member (folderPersonaId = 3)
                const existingMember = allUsers.find(
                    (user) => user.id === newMemberId
                );

                const isMember = existingMember?.folderPersonaId === 3;

                if (existingMember && isMember) {
                    // User is already a member, use modify API
                    const modifyRequest: ModifyFolderPersonasRequest = {
                        users: [assignment],
                    };

                    modifyFolderPersonas(
                        { folderId: folderId, data: modifyRequest },
                        {
                            onSuccess: () => {
                                setMembers([
                                    ...members,
                                    {
                                        ...newMember,
                                        persona: folderRole as 'admin' | 'user',
                                    },
                                ]);
                                showToast(
                                    l('_success'),
                                    l('_folderMemberAdded'),
                                    'success'
                                );
                                onSuccess?.();
                                onClose();
                            },
                            onError: (error) => {
                                showGenericErrorToast(error);
                            },
                        }
                    );
                } else {
                    // User is not in this folder at all, use add API
                    const requestData: AddFolderPersonasRequest = {
                        // folderId: folderId,
                        users: [assignment],
                    };

                    addFolderPersonas(
                        { folderId: folderId, data: requestData },
                        {
                            onSuccess: () => {
                                setMembers([...members, newMember]);
                                showToast(
                                    l('_success'),
                                    l('_folderMemberAdded'),
                                    'success'
                                );
                                onSuccess?.();
                                onClose();
                            },
                            onError: (error) => {
                                showGenericErrorToast(error);
                            },
                        }
                    );
                }
                setNewMemberId('');
            }
        }
    };

    const handleDeleteMember = (memberId: number) => {
        const params: PutV1FolderDeletePersonaParams = {
            personaUserId: memberId,
            folderId: folderId,
            deleteAll: deleteAll ? true : false,
        };

        deletePersona(
            { folderId, params },
            {
                onSuccess: () => {
                    setMembers(
                        members.filter((member) => member.id !== memberId)
                    );
                    setVisibleDeleteModal(false);
                    showToast(
                        l('_success'),
                        l('_folderMemberRemoved'),
                        'success'
                    );
                    onSuccess?.();
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            }
        );
    };

    const handleSubmit = () => {
        const assignments: FolderPersonaAssignmentDTO[] = members
            .filter((x) => x.newPersona)
            .map((member) => ({
                userId: member.id,
                folderPersona:
                    member.newPersona === 'admin'
                        ? 1
                        : member.newPersona === 'user'
                        ? 2
                        : 3, // Map role to folderPersona
            }));

        const modifyRequest: ModifyFolderPersonasRequest = {
            users: assignments,
        };

        modifyFolderPersonas(
            { folderId: folderId, data: modifyRequest },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        l('_folderRolesUpdated'),
                        'success'
                    );
                    onSuccess?.();
                    onClose();
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            }
        );
    };

    const deleteModalContent = () => {
        return (
            <div className="mt-3">
                {members.length && (
                    <p>
                        Please select the one you wanted to delete{' '}
                        <b>{members[0].name}</b> from
                    </p>
                )}

                <RadioGroup
                    onChange={(value: number) => setDeleteAll(value)}
                    className="mt-3"
                    defaultValue={deleteAll}
                >
                    <Radio value={0}>
                        Remove from just <b>this folder</b>
                    </Radio>
                    <Radio value={1}>
                        Remove from <b>this folder and all child folders</b>
                    </Radio>
                </RadioGroup>
                <Button
                    className="mt-4 m-auto"
                    kind="tertiary"
                    onClick={() => handleDeleteMember(members[0].id)}
                >
                    Remove
                </Button>
            </div>
        );
    };

    return (
        <Modal
            visible={visible}
            onCancel={() => onClose()}
            title={'Manage access'}
            modalStyle={{ width: '33.75rem' }}
        >
            <div className="manage-access-module">
                {/* <div className="folder-name-display">
                    <span className="folderName">{folderData.name}</span>
                </div> */}
                <div className="ap-grid m-0">
                    <div className="g-col-9">
                        <div className="search-sample">
                            <Search
                                searchType={'secondary'}
                                data={potentialMembers.map((data) => ({
                                    value: data.id,
                                    label: data.email || '',
                                }))}
                                placeholder={'Invite others by entering email'}
                                onSelect={(value) => {
                                    setNewMemberId(Number(value));
                                }}
                            />
                        </div>
                    </div>
                    <div className="floating-dropdown mt-2">
                        <Select
                            hideTitleOnInput={true}
                            data={roleOptions}
                            value={folderRole}
                            onSelect={(e: any) => {
                                setFolderRole(e);
                            }}
                        ></Select>
                    </div>
                    <div className="g-col-3 mt-2">
                        <Button onClick={handleAddMember}>Invite</Button>
                    </div>
                </div>
                <div className="position-relative">
                    <p className="ap-font-weight-1 ap-text-gray mt-3 mb-3">
                        Who has access
                    </p>
                    <hr className="line-word" />
                </div>
                {members.map((member) => (
                    <div className="ap-grid mrl-0 mt-2 mb-2" key={member.id}>
                        <div className="g-col-1">
                            <Avatar
                                label={member.name
                                    ?.substring(0, 1)
                                    .toLocaleUpperCase()}
                                bgColor="gray"
                            />
                        </div>
                        <div className="g-col-5 mt-2">
                            <span>{member.name}</span>
                        </div>
                        <div className="g-col-4">
                            <Select
                                disabled={!canChangeRoleCondition(member)}
                                hideTitleOnInput={true}
                                data={roleOptions}
                                value={member.newPersona || member.persona}
                                onSelect={(e) =>
                                    handlePersonaChange(
                                        member.id,
                                        e as 'admin' | 'user'
                                    )
                                }
                            />
                        </div>
                        <div className="g-col-1 mt-2">
                            <i
                                onClick={() => openDeleteModal(member)}
                                className={`Appkit4-icon icon-delete-fill ${
                                    cannotDeleteCondition(member)
                                        ? 'disabled-icon'
                                        : ''
                                }`}
                            />
                        </div>
                        <div className="g-col-1 mt-2">
                            {!canChangeRoleCondition(member) && (
                                <Tooltip
                                    trigger="hover"
                                    position="top-left"
                                    distance={4}
                                    id="tooltipDesc"
                                    appendAfterTarget={true}
                                    content={`${member.name} cannot be changed from admin to user because they are an admin on the parent folder`}
                                >
                                    <i
                                        className={`Appkit4-icon icon-information-fill`}
                                    />
                                </Tooltip>
                            )}
                            {cannotDeleteCondition(member) &&
                                canChangeRoleCondition(member) && (
                                    <Tooltip
                                        trigger="hover"
                                        position="top-left"
                                        distance={4}
                                        id="tooltipDesc"
                                        appendAfterTarget={true}
                                        content={`${member.name} cannot be removed as he is admin / user on a parent folder`}
                                    >
                                        <i
                                            className={`Appkit4-icon icon-information-fill`}
                                        />
                                    </Tooltip>
                                )}
                        </div>
                    </div>
                ))}
                {members.length == 0 && (
                    <div className="ap-grid mt-4 mb-4">
                        <div className="g-col-12">
                            <img className="ms-5" src={SolutionCenter} />
                        </div>
                        <p className="g-col-12 m-auto mt-4 mb-4">
                            No users yet
                        </p>
                    </div>
                )}
                {deleteMemberId && (
                    <DeleteModal
                        content={deleteModalContent()}
                        visible={visibleDeleteModal}
                        onClose={() => {
                            setVisibleDeleteModal(false);
                        }}
                        title={'Remove User'}
                    />
                )}
            </div>
            <div className="edit-btn-container">
                <Button
                    onClick={handleSubmit}
                    kind="danger"
                    disabled={members.filter((x) => x.newPersona).length === 0}
                    style={{ marginTop: '40px' }}
                >
                    Submit
                </Button>
            </div>
        </Modal>
    );
};

export default FolderConfig;
